import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const TermOfServiceAndConditions: React.FC = () => {
  const theme = useTheme();

  return (
    <Container maxWidth="md" sx={{ py: 4, minHeight: "100vh" }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: theme.shadows[4],
        }}
      >
        {/* Header Section */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Terms of Service and Conditions
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Last updated: February 2025
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Acceptance of Terms */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">1. Acceptance of Terms</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              By using our website, you acknowledge that you have read, understood, and agreed to be legally bound by these terms. 
              These terms apply to all visitors, donors, volunteers, and other users who engage with Hope Beyond International.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Modification of Terms */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">2. Modification of Terms</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Hope Beyond International reserves the right to modify, update, or revise these Terms of Service at any time. 
              We will notify users of significant changes through our website. Your continued use of our website after modifications 
              indicates your acceptance of the revised terms.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Contact Information */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">3. Contact Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Hope Beyond International is a registered non-profit organization. For inquiries, donations, volunteer opportunities, or patient services, 
              please visit our{" "}
              <Link href="/contact-us" underline="hover">
                Contact Us
              </Link>{" "}
              page.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Permitted Use */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">4. Permitted Use of Our Website</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              By accessing this website, you agree to use it for lawful purposes only. You must not:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Engage in fraudulent or misleading activity." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Attempt to gain unauthorized access to our systems or disrupt website functionality." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Use our content or branding for commercial purposes without prior written consent." />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Donations & Payments */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">5. Donations & Payment Processing</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Hope Beyond International accepts donations securely through{" "}
              <Link href="https://stripe.com/" target="_blank" rel="noopener" underline="hover">
                Stripe
              </Link>. We do not store any financial information on our servers. 
              All payment transactions are encrypted and processed securely by Stripe.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Privacy Policy */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">6. Privacy Policy & Data Collection</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Hope Beyond International is committed to protecting your privacy. We only collect basic contact information when users voluntarily submit inquiries, donate, or apply for volunteer opportunities.
            </Typography>
            <Typography variant="body1">
              For more details, please review our{" "}
              <Link href="/privacy-policy" underline="hover">
                Privacy Policy
              </Link>.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Limitation of Liability */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">7. Limitation of Liability</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Hope Beyond International provides resources and guidance but does not offer medical diagnoses or treatments. We are not responsible for any direct, indirect, incidental, or consequential damages that arise from the use of our website or services.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Termination of Service */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">8. Termination of Service</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Hope Beyond International reserves the right to restrict access to any user who violates these terms.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="textSecondary" textAlign="center">
          If you have any questions, please contact us at{" "}
          <Link href="mailto:info@hbinternational.org" underline="hover">
            info@hbinternational.org
          </Link>.
        </Typography>
      </Paper>
    </Container>
  );
};

export default TermOfServiceAndConditions;
